/**
 * Bottom sheet. SPEC.md §19, §17.3, §47.
 *
 * Round setup, round completion, player admin and history are all sheets or
 * drawers rather than routes, so this is the app's only modal surface. §47
 *
 * It does the four things a hand-rolled modal usually gets wrong: focus moves
 * in on open and back out on close, Tab is trapped inside, Escape closes, and
 * the bottom padding respects the home indicator. §19, §17.3
 */

import { useCallback, useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from './IconButton';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** How many sheets are open, so nested opens don't unlock the body early. */
let openSheets = 0;

export interface SheetProps {
  open: boolean;
  onClose(): void;
  /** Rendered as the heading and used as the dialog's accessible name. */
  title: string;
  description?: string;
  /** Pinned below the scrolling body — primary actions belong here. */
  footer?: ReactNode;
  /**
   * Drawn in the dimmed space *above* the panel, over the scrim. §19, §47
   *
   * A sheet never fills the screen — it caps at 86dvh and sits on the bottom
   * edge — so there is always a band of darkened page above it, and until now it
   * did one job: it was the thing you tapped to get out. It can do two. What
   * belongs here is something the sheet is *about* that wants to be bigger than
   * the sheet has room for, which on a picker means the thing being picked.
   *
   * It is decoration and is treated as such: rendered outside the dialog so the
   * focus trap and the accessible name are untouched, and `pointer-events: none`
   * so the tap that used to dismiss the sheet still does. Anything that needs to
   * be read or pressed belongs in `children` or `footer`. §19
   */
  aside?: ReactNode;
  children: ReactNode;
}

export function Sheet({
  open,
  onClose,
  title,
  description,
  footer,
  aside,
  children,
}: SheetProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusTo = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    if (!open) return;

    restoreFocusTo.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Focus the panel so the title and description are announced, then let Tab
    // walk into the content. A sheet whose first job is typing can opt into
    // taking focus directly with data-autofocus. §19
    const panel = panelRef.current;
    const preferred = panel?.querySelector<HTMLElement>('[data-autofocus]');
    (preferred ?? panel)?.focus();

    openSheets += 1;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      openSheets -= 1;
      if (openSheets === 0) document.body.style.overflow = previousOverflow;
      restoreFocusTo.current?.focus();
    };
  }, [open]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const panel = panelRef.current;
      if (!panel) return;
      const focusable = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)];
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div
        role="presentation"
        onClick={onClose}
        className="absolute inset-0 animate-fade-in bg-black/60"
      />

      {/*
        Over the scrim and under nothing. It is a sibling of the dialog rather
        than a child, which is what keeps it out of the accessible name, out of
        the focus trap and out of the sheet's own scroll area — and it is inert,
        so the scrim it is drawn on top of still closes the sheet when tapped.
        `min-h-0` because the panel's height is what it is and this takes the
        remainder; on the shortest viewport that remainder is 14dvh. §19, §18.2
      */}
      {aside ? (
        <div
          aria-hidden="true"
          className="pointer-events-none relative flex min-h-0 flex-1 flex-col justify-end"
        >
          {aside}
        </div>
      ) : null}

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="relative flex max-h-[86dvh] w-full flex-col gap-4 animate-sheet-in rounded-t-sheet border-t border-line bg-surface px-edge pt-4 pb-safe shadow-sheet"
      >
        <div aria-hidden="true" className="mx-auto h-1 w-10 shrink-0 rounded-full bg-line" />

        <div className="flex shrink-0 items-start gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h2 id={titleId} className="text-lg font-semibold text-ink">
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="text-sm text-ink-muted">
                {description}
              </p>
            ) : null}
          </div>
          <IconButton label="Close" onClick={onClose}>
            ✕
          </IconButton>
        </div>

        <div className="scroll-area -mx-edge flex-1 px-edge">{children}</div>

        {footer ? <div className="flex shrink-0 flex-col gap-2">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
